/**
 * Encabezado del Resumen: quien es esta tienda y en que estado esta.
 *
 * Casi todo sale del objeto `Store` que la pantalla ya tiene en memoria (lo
 * confirmo el selector de tiendas con un GET que devuelve 403 si no esta
 * concedida), asi que se dibuja sin esperar ninguna request. La unica pieza que
 * viene de la API de monitoreo es `has_ml_tokens`, y por eso llega como
 * `boolean | null`: mientras no se sabe, se dice que no se sabe, en vez de
 * mostrar "sin vincular" y mandar a alguien a rehacer un OAuth que esta bien.
 */

import type { Store } from "../../api/types";
import { Badge } from "./ui";

/**
 * Los tres interruptores que explican, casi siempre, por que algo no pasa.
 *
 * `sync_enabled` en `false` significa que la tienda no se sincroniza aunque las
 * credenciales esten perfectas, y `has_ml_tokens` en `false` significa que no
 * puede ni empezar. Son las dos causas mas comunes de "no aparece nada nuevo",
 * y estan arriba para que no haya que ir a buscarlas.
 */
export function StoreHealth({
  store,
  hasMlTokens,
}: {
  readonly store: Store;
  readonly hasMlTokens: boolean | null;
}) {
  return (
    <>
      <div className="home-badges">
        <Badge
          tone={store.is_active ? "ok" : "danger"}
          title="stores.is_active. Una tienda inactiva sigue teniendo datos, pero queda fuera de los procesos automaticos."
        >
          {store.is_active ? "Tienda activa" : "Tienda inactiva"}
        </Badge>

        <Badge
          tone={store.sync_enabled ? "ok" : "warn"}
          title="stores.sync_enabled. Si esta en false, el catalogo no se actualiza desde MercadoLibre aunque las credenciales esten bien."
        >
          {store.sync_enabled ? "Sincronizacion habilitada" : "Sincronizacion deshabilitada"}
        </Badge>

        {hasMlTokens === null ? (
          <Badge tone="muted" title="Todavia no respondio GET /api/v1/monitoring/summary.">
            Cuenta ML: consultando
          </Badge>
        ) : (
          <Badge
            tone={hasMlTokens ? "ok" : "danger"}
            title={
              hasMlTokens
                ? "Hay un ml_access_token guardado. Que siga siendo valido solo lo sabe MercadoLibre: el backend no devuelve el token ni lo prueba desde aca."
                : "No hay ml_access_token guardado: la sincronizacion y la publicacion no pueden ni empezar. Se resuelve con scripts/oauth/get_ml_authorization.py."
            }
          >
            {hasMlTokens ? "Cuenta ML vinculada" : "Sin token de MercadoLibre"}
          </Badge>
        )}

        <Badge
          tone={store.auto_pricing_enabled ? "neutral" : "muted"}
          title="stores.auto_pricing_enabled. Hoy no hay ninguna corrida de repricing registrada: ver 'Modulos sin datos' mas abajo."
        >
          Pricing automatico: {store.auto_pricing_enabled ? "si" : "no"}
        </Badge>
      </div>

      <dl className="meta">
        <div>
          <dt>Id de tienda</dt>
          <dd>
            <code>{store.id}</code>
          </dd>
        </div>
        <div>
          <dt>Slug</dt>
          <dd>
            <code>{store.slug}</code>
          </dd>
        </div>
        <div>
          <dt>Sitio ML</dt>
          <dd>{store.ml_site_id}</dd>
        </div>
        <div>
          <dt>Vendedor ML</dt>
          <dd>{store.ml_user_id ?? <span className="home-muted">sin vincular</span>}</dd>
        </div>
      </dl>
    </>
  );
}
